import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Grid,
  Button,
  Paper,
  Collapse,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { getCurrencySymbol } from '../utils/currency';
import { translateCategory } from '../utils/categoryTranslations';

export interface TaskFiltersValues {
  search: string;
  category_id: string;
  status: string;
  min_budget: string;
  max_budget: string;
  sort_by: string;
  sort_order: string;
}

interface TaskFiltersProps {
  onFiltersChange: (filters: TaskFiltersValues) => void;
  userType?: 'client' | 'professional';
}

interface Category {
  id: number;
  name: string;
  icon?: string;
}

const TaskFilters: React.FC<TaskFiltersProps> = ({ onFiltersChange, userType = 'client' }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(!isMobile);
  const [categories, setCategories] = useState<Category[]>([]);

  const [filters, setFilters] = useState<TaskFiltersValues>({
    search: '',
    category_id: '',
    status: '',
    min_budget: '',
    max_budget: '',
    sort_by: 'created_at',
    sort_order: 'desc',
  });

  // Зареждане на категориите
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/v1/categories');
        // API връща { items: [...] }, не директно масив
        const categoriesData = response.data?.items || response.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  const handleFilterChange = (field: keyof TaskFiltersValues, value: string) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(filters);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApplyFilters();
    }
  };

  const handleClearFilters = () => {
    const clearedFilters: TaskFiltersValues = {
      search: '',
      category_id: '',
      status: '',
      min_budget: '',
      max_budget: '',
      sort_by: 'created_at',
      sort_order: 'desc',
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.category_id) count++;
    if (filters.status) count++;
    if (filters.min_budget) count++;
    if (filters.max_budget) count++;
    return count;
  };

  const statusOptions = userType === 'professional'
    ? [
        { value: 'open', label: t('tasks.status.open') },
        { value: 'in_progress', label: t('tasks.status.in_progress') },
        { value: 'completed', label: t('tasks.status.completed') },
        { value: 'cancelled', label: t('tasks.status.cancelled') },
      ]
    : [
        { value: 'open', label: t('tasks.status.open') },
        { value: 'in_progress', label: t('tasks.status.in_progress') },
        { value: 'completed', label: t('tasks.status.completed') },
        { value: 'cancelled', label: t('tasks.status.cancelled') },
      ];

  const sortOptions = [
    { value: 'created_at', label: t('tasks.sort.date') },
    { value: 'budget', label: t('tasks.sort.budget') },
    { value: 'deadline', label: t('tasks.sort.deadline') },
  ];

  const activeFiltersCount = getActiveFiltersCount();
  const currencySymbol = getCurrencySymbol(i18n.language);
  const localizedCategories = useMemo(() => {
    const dedupedByLabel = new Map<string, Category>();

    categories.forEach((category) => {
      const localizedName = translateCategory(category.name, i18n.language);
      const dedupeKey = localizedName.trim().toLowerCase();
      if (!dedupedByLabel.has(dedupeKey)) {
        dedupedByLabel.set(dedupeKey, category);
      }
    });

    return Array.from(dedupedByLabel.values()).sort((left, right) =>
      translateCategory(left.name, i18n.language).localeCompare(
        translateCategory(right.name, i18n.language),
        i18n.language
      )
    );
  }, [categories, i18n.language]);

  return (
    <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }} data-testid="task-filters">
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
          cursor: isMobile ? 'pointer' : 'default',
        }}
        onClick={isMobile ? () => setExpanded(!expanded) : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('tasks.filters.title')}
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip
              label={activeFiltersCount}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        {isMobile && (
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      {/* Filters Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 3 }} onKeyDown={handleKeyDown}>
          <Grid container spacing={2}>
            {/* Search */}
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                fullWidth
                label={String(t('tasks.filters.search'))}
                placeholder={String(t('tasks.filters.searchPlaceholder'))}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
                inputProps={{ 'data-testid': 'task-filters-search-input' }}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                fullWidth
                select
                label={String(t('tasks.filters.category'))}
                value={filters.category_id}
                onChange={(e) => handleFilterChange('category_id', e.target.value)}
              >
                <MenuItem value="">
                  <em>{String(t('tasks.filters.allCategories'))}</em>
                </MenuItem>
                {localizedCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id.toString()}>
                    {translateCategory(category.name, i18n.language)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                fullWidth
                select
                label={String(t('tasks.filters.status'))}
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">
                  <em>{String(t('tasks.filters.allStatuses'))}</em>
                </MenuItem>
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Min Budget */}
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                fullWidth
                type="number"
                label={String(t('tasks.filters.minBudget'))}
                placeholder="0"
                value={filters.min_budget}
                onChange={(e) => handleFilterChange('min_budget', e.target.value)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>{currencySymbol}</Typography>,
                }}
              />
            </Grid>

            {/* Max Budget */}
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                fullWidth
                type="number"
                label={String(t('tasks.filters.maxBudget'))}
                placeholder="10000"
                value={filters.max_budget}
                onChange={(e) => handleFilterChange('max_budget', e.target.value)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>{currencySymbol}</Typography>,
                }}
              />
            </Grid>

            {/* Sort By */}
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                fullWidth
                select
                label={String(t('tasks.filters.sortBy'))}
                value={filters.sort_by}
                onChange={(e) => handleFilterChange('sort_by', e.target.value)}
              >
                {sortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Sort Order */}
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                fullWidth
                select
                label={String(t('tasks.filters.sortOrder'))}
                value={filters.sort_order}
                onChange={(e) => handleFilterChange('sort_order', e.target.value)}
              >
                <MenuItem value="desc">{String(t('tasks.filters.descending'))}</MenuItem>
                <MenuItem value="asc">{String(t('tasks.filters.ascending'))}</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={activeFiltersCount === 0}
            >
              {t('tasks.filters.clear')}
            </Button>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleApplyFilters}
              data-testid="task-filters-apply-button"
            >
              {t('tasks.filters.apply')}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default TaskFilters;
